import { useState, useEffect, useRef } from 'react';
import { FlaskConical, Upload, Database, Folder, Play, Trash2, BarChart3, Tag, AlertTriangle, Loader2 } from 'lucide-react';
import '../../tailwindcss/Tailwind.css';

const CLUSTER_BASE = process.env.REACT_APP_CLUSTER_API_URL ||
  (process.env.REACT_APP_NVIDIA_GPU_API_URL
    ? `https://${process.env.REACT_APP_NVIDIA_GPU_API_URL}:8001`
    : 'http://18.208.58.92:8001');

const clusterApi = (path) => `${CLUSTER_BASE}${path}`;

const TABS = [
  { key: 'overview',  Icon: BarChart3,      label: 'Overview' },
  { key: 'themes',    Icon: Tag,            label: 'Cluster Themes' },
  { key: 'anomalies', Icon: AlertTriangle,  label: 'Anomalies' },
];

export default function Clustering() {
  const [dataSource, setDataSource] = useState('file');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [sqlQuery, setSqlQuery] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [themes, setThemes] = useState([]);
  const [anomalies, setAnomalies] = useState(null);
  const [saveDir, setSaveDir] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const fileInputRef = useRef(null);

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    try {
      const res = await fetch(clusterApi('/api/analyses'));
      setHistory((await res.json()).analyses || []);
    } catch { /* ignore */ }
  };

  const loadResults = async (dir) => {
    const [sumRes, themesRes, anomRes] = await Promise.all([
      fetch(clusterApi(`/api/download/summary_statistics.json?save_dir=${encodeURIComponent(dir)}`)),
      fetch(clusterApi(`/api/download/cluster_themes.json?save_dir=${encodeURIComponent(dir)}`)),
      fetch(clusterApi(`/api/download/anomaly_explanations.json?save_dir=${encodeURIComponent(dir)}`)),
    ]);
    if (sumRes.ok) setSummary(await sumRes.json());
    if (themesRes.ok) setThemes(await themesRes.json());
    if (anomRes.ok) setAnomalies(await anomRes.json());
  };

  const handleAnalyze = async () => {
    setError(''); setIsAnalyzing(true); setSummary(null); setThemes([]); setAnomalies(null);
    try {
      let res;
      if (dataSource === 'file') {
        if (!file) { setError('Please select a file first.'); setIsAnalyzing(false); return; }
        const fd = new FormData(); fd.append('file', file);
        res = await fetch(clusterApi('/cluster/upload'), { method: 'POST', body: fd });
      } else {
        if (!sqlQuery.trim()) { setError('Please enter a SQL query.'); setIsAnalyzing(false); return; }
        res = await fetch(clusterApi('/cluster/upload-sql'), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: sqlQuery }),
        });
      }
      const { save_dir } = await res.json();
      setSaveDir(save_dir);
      await loadResults(save_dir);
      setActiveTab('overview');
      loadHistory();
    } catch (err) {
      setError(err.message || 'Analysis failed. Please check the cluster service is running.');
    } finally { setIsAnalyzing(false); }
  };

  const loadAnalysis = async (dir) => {
    setSaveDir(dir);
    await loadResults(dir);
    setActiveTab('overview');
  };

  const deleteAnalysis = async (dir) => {
    if (!window.confirm('Delete this analysis?')) return;
    await fetch(clusterApi(`/api/analyses/${encodeURIComponent(dir)}`), { method: 'DELETE' });
    if (saveDir === dir) { setSaveDir(null); setSummary(null); setThemes([]); setAnomalies(null); }
    loadHistory();
  };

  return (
    <div className="flex flex-col h-full">
      <p className="px-6 pt-4 pb-0 text-[0.8rem] italic text-gray-600">AI-powered segmentation &amp; anomaly detection</p>
      <div className="flex-1 p-4 overflow-hidden">
        <div className="flex h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Sidebar */}
      <div className="w-64 border-r border-gray-200 flex flex-col shrink-0">
        <div className="px-4 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
            <FlaskConical className="h-5 w-5" /> Clustering
          </h2>

          {/* Source toggle */}
          <div className="flex gap-1.5 mt-3 mb-3">
            {[['file', Folder, 'File'], ['sql', Database, 'SQL']].map(([src, Icon, label]) => (
              <button key={src} onClick={() => setDataSource(src)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  dataSource === src ? 'bg-primaryLight text-primary border-primary/30' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>

          {dataSource === 'file' ? (
            <>
              <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.json" className="hidden" onChange={e => { const f = e.target.files[0]; if (f) { setFile(f); setFileName(f.name); setError(''); } }} />
              <button onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-xs text-gray-500 hover:border-primary hover:text-primary transition-colors">
                <Upload className="h-3.5 w-3.5" />
                {fileName || 'Select CSV / Excel / JSON'}
              </button>
            </>
          ) : (
            <textarea value={sqlQuery} onChange={e => setSqlQuery(e.target.value)}
              placeholder="SELECT * FROM customers LIMIT 1000" rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none" />
          )}

          {error && <p className="text-xs text-red-600 mt-2">{error}</p>}

          <button onClick={handleAnalyze} disabled={isAnalyzing}
            className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primaryDark disabled:opacity-50 transition-colors">
            {isAnalyzing ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing...</> : <><Play className="h-4 w-4" /> Run Analysis</>}
          </button>
        </div>

        {/* History */}
        <div className="flex-1 overflow-y-auto p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2 px-1">History</p>
          {history.map((h, i) => (
            <div key={i} onClick={() => loadAnalysis(h.save_dir)}
              className={`p-2.5 rounded-lg border mb-1.5 cursor-pointer transition-colors ${
                saveDir === h.save_dir ? 'border-primary/30 bg-primaryLight' : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}>
              <p className={`text-xs font-medium truncate ${saveDir === h.save_dir ? 'text-primary' : 'text-gray-700'}`}>{h.dataset_name}</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-[10px] text-gray-400">{h.n_clusters} clusters · {h.total_records?.toLocaleString()} rows</p>
                <button onClick={e => { e.stopPropagation(); deleteAnalysis(h.save_dir); }}
                  className="p-0.5 text-gray-300 hover:text-red-500 transition-colors">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
          {!history.length && <p className="text-xs text-gray-400 px-1">No past analyses.</p>}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {summary ? (
          <>
            {/* Stats */}
            <div className="border-b border-gray-200 bg-white shrink-0">
              <div className="px-6 py-4 flex items-center gap-4">
                {[['Total Records', summary.total_records?.toLocaleString()], ['Clusters', summary.n_clusters], ['Silhouette Score', summary.silhouette_score?.toFixed(3)], ['Anomalies', summary.final_true_anomalies]].map(([label, value]) => (
                  <div key={label} className="flex-1 bg-white border border-gray-200 rounded-lg p-3 text-center shadow-sm">
                    <p className="text-2xl font-bold text-primary">{value ?? '—'}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
              <div className="flex px-6">
                {TABS.map(({ key, Icon, label }) => (
                  <button key={key} onClick={() => setActiveTab(key)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === key ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-800'
                    }`}>
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'overview' && (
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Cluster Size Distribution</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {Object.entries(summary.cluster_sizes || {}).map(([cluster, count]) => (
                      <div key={cluster} className="bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm">
                        <p className="text-2xl font-bold text-primary">{count}</p>
                        <p className="text-xs text-gray-500 mt-1">Cluster {cluster}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {activeTab === 'themes' && (
                <div className="space-y-4">
                  {themes.map((theme, i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                      <h4 className="font-semibold text-gray-900 mb-1">{theme.intelligent_label || theme.theme_title || `Cluster ${i}`}</h4>
                      <p className="text-sm text-gray-600 mb-3">{theme.summary || theme.theme}</p>
                      {theme.top_terms?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {theme.top_terms.slice(0, 8).map((t, j) => (
                            <span key={j} className="text-xs px-2.5 py-1 rounded-full bg-primaryLight text-primary border border-primary/20">
                              {typeof t === 'object' ? t.term : t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {!themes.length && <p className="text-sm text-gray-500">No cluster themes generated yet.</p>}
                </div>
              )}
              {activeTab === 'anomalies' && (
                <div>
                  {anomalies ? (
                    <>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-semibold text-red-800">{anomalies.total_outliers} anomalies detected</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {Object.entries(anomalies.by_cluster_counts || {}).map(([cluster, count]) => (
                              <span key={cluster} className="text-xs px-2.5 py-0.5 rounded-full bg-red-100 text-red-700">Cluster {cluster}: {count}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {(anomalies.items || []).slice(0, 10).map((item, i) => (
                          <div key={i} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap">{JSON.stringify(item, null, 2)}</pre>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : <p className="text-sm text-gray-500">No anomaly data available.</p>}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="p-5 bg-primaryLight rounded-full mb-5">
              <FlaskConical className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Customer Segmentation &amp; Clustering</h2>
            <p className="text-sm text-gray-500 max-w-md">Upload a dataset or enter a SQL query in the sidebar, then click Run Analysis to discover customer clusters and anomalies.</p>
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
}
